import { useEffect, useRef, useState } from 'react';
import {
  Alert,
  LoadingState,
  V2Badge,
  V2Button,
  V2Card,
  V2CardHeader,
} from '@sakhtar/design-system/react';
import { apiGet, apiPost, ApiError } from '../../lib/apiClient.js';
import {
  openingPreparationError,
  openingItemMapping,
  type OpeningAction,
  type OpeningBatch,
  type OpeningMapping,
} from '../../lib/dataImport/openingImport.js';

const labels = {
  draft: 'پیش‌نویس',
  prepared: 'تطبیق‌شده',
  submitted: 'ارسال‌شده برای تأیید',
  approved: 'تأییدشده',
  posted: 'ثبت قطعی',
};
const actionLabels = {
  prepare: 'ثبت تطبیق و نگاشت حساب‌ها',
  submit: 'ارسال برای تأیید',
  approve: 'تأیید مستقل',
  post: 'ثبت قطعی افتتاحیه',
};

/** Opening review is independent of the creator-private import workspace. */
export function OpeningImportReview({
  base,
  batchId,
}: {
  readonly base: string;
  readonly batchId: string;
}) {
  const [batch, setBatch] = useState<OpeningBatch | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [loading, setLoading] = useState(true);
  const [year, setYear] = useState('');
  const [period, setPeriod] = useState('');
  const [evidence, setEvidence] = useState('');
  const [mappings, setMappings] = useState<Record<string, OpeningMapping>>({});
  const [confirmed, setConfirmed] = useState(false);
  const epoch = useRef(0);
  const locked = useRef(false);
  const mutation = useRef<AbortController | null>(null);
  const requests = useRef<Record<string, { signature: string; key: string }>>({});
  const path = `${base}/api/v1/accounting/opening-import-batches/${encodeURIComponent(batchId)}`;
  useEffect(() => {
    const current = ++epoch.current;
    mutation.current?.abort();
    const controller = new AbortController();
    setLoading(true);
    setBatch(null);
    setError(null);
    setBusy(false);
    locked.current = false;
    setYear('');
    setPeriod('');
    setEvidence('');
    setMappings({});
    setConfirmed(false);
    requests.current = {};
    void apiGet<OpeningBatch>(path, { signal: controller.signal })
      .then((result) => {
        if (current === epoch.current) {
          setBatch(result);
          setEvidence(result.evidence ?? '');
          setYear(result.fiscalYearId ?? '');
          setPeriod(result.fiscalPeriodId ?? '');
          setMappings(
            Object.fromEntries(result.items.map((item) => [item.itemId, openingItemMapping(item)])),
          );
        }
      })
      .catch((cause) => {
        if (current === epoch.current && !controller.signal.aborted)
          setError(
            cause instanceof ApiError && cause.status === 403
              ? 'برای مشاهدهٔ این افتتاحیه دسترسی ندارید.'
              : 'افتتاحیه دریافت نشد؛ صفحه را دوباره باز کنید.',
          );
      })
      .finally(() => {
        if (current === epoch.current) setLoading(false);
      });
    return () => {
      epoch.current += 1;
      controller.abort();
      mutation.current?.abort();
    };
  }, [path]);
  function change(itemId: string, patch: Partial<OpeningMapping>) {
    if (locked.current) return;
    setMappings((all) => ({
      ...all,
      [itemId]: {
        ...(all[itemId] ?? {
          itemId,
          controlAccountId: '',
          counterpartAccountId: '',
        }),
        ...patch,
      },
    }));
  }
  async function act(action: OpeningAction) {
    if (!batch || locked.current || !batch.actions[action]) return;
    const body =
      action === 'prepare'
        ? {
            fiscalYearId: year,
            fiscalPeriodId: period,
            mappings: Object.values(mappings).map(
              ({ controlDimensions, counterpartDimensions, ...mapping }) => ({
                ...mapping,
                controlDimensions: Object.entries(controlDimensions ?? {})
                  .filter(([, value]) => value)
                  .map(([dimensionId, dimensionValueId]) => ({
                    dimensionId,
                    dimensionValueId,
                  })),
                counterpartDimensions: Object.entries(counterpartDimensions ?? {})
                  .filter(([, value]) => value)
                  .map(([dimensionId, dimensionValueId]) => ({
                    dimensionId,
                    dimensionValueId,
                  })),
              }),
            ),
            evidence: evidence.trim(),
          }
        : {};
    if (action === 'prepare') {
      const invalid = openingPreparationError(
        batch,
        year,
        period,
        Object.values(mappings),
        evidence,
      );
      if (invalid) {
        setError(invalid);
        return;
      }
    }
    if (
      action === 'post' &&
      (!confirmed || (batch.sensitiveAction.requiresMfa && !batch.sensitiveAction.available))
    )
      return;
    const current = epoch.current;
    locked.current = true;
    setBusy(true);
    setError(null);
    const signature = JSON.stringify(body);
    const saved = requests.current[action];
    const key = saved?.signature === signature ? saved.key : crypto.randomUUID();
    requests.current[action] = { signature, key };
    const controller = new AbortController();
    mutation.current = controller;
    try {
      await apiPost(`${path}/${action}`, body, {
        headers: { 'idempotency-key': key },
        signal: controller.signal,
      });
      if (current !== epoch.current) return;
      const refreshed = await apiGet<OpeningBatch>(path, {
        signal: controller.signal,
      });
      if (current !== epoch.current) return;
      setBatch(refreshed);
      setConfirmed(false);
      delete requests.current[action];
    } catch (cause) {
      if (current === epoch.current)
        setError(
          cause instanceof ApiError && cause.status === 403
            ? 'این اقدام برای شما مجاز نیست؛ تأییدکننده باید مستقل از سازنده باشد.'
            : cause instanceof Error
              ? cause.message
              : 'عملیات انجام نشد. تلاش مجدد با همان شناسهٔ درخواست انجام می‌شود.',
        );
    } finally {
      if (current === epoch.current) {
        locked.current = false;
        setBusy(false);
      }
    }
  }
  if (loading) return <LoadingState />;
  if (!batch) return <Alert tone="danger">{error ?? 'افتتاحیه در دسترس نیست.'}</Alert>;
  const editable = batch.actions.prepare && !busy;
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
      <V2Card>
        <V2CardHeader title="تکمیل افتتاحیهٔ واردشده" />
        <div
          style={{
            padding: 20,
            display: 'flex',
            flexDirection: 'column',
            gap: 12,
          }}
        >
          <V2Badge tone={batch.status === 'posted' ? 'success' : 'info'}>
            {labels[batch.status]}
          </V2Badge>
          <p>
            تطبیق ← ارسال ← تأیید مستقل ← ثبت قطعی. تا ثبت قطعی، ماندهٔ حسابداری و موجودی عملیاتی
            تغییر نمی‌کند.
          </p>
          <p>
            کد پیگیری: <span style={{ overflowWrap: 'anywhere' }}>{batch.batchId}</span>
          </p>
          {error ? <Alert tone="danger">{error}</Alert> : null}
          {batch.status === 'posted' ? (
            <Alert tone="success">
              افتتاحیه ثبت قطعی شد. اصلاح بعدی فقط از مسیر اصلاحی مجاز انجام می‌شود.
            </Alert>
          ) : null}
          {batch.sensitiveAction.requiresMfa && !batch.sensitiveAction.available ? (
            <Alert tone="warning" title="ثبت قطعی نیازمند احراز هویت تکمیلی است">
              {batch.sensitiveAction.reason ??
                'احراز هویت یک‌بارمصرف این عملیات هنوز در دسترس نیست؛ ثبت قطعی تا فراهم‌شدن مسیر امن مسدود است.'}
            </Alert>
          ) : null}
          {!batch.actions.prepare && (batch.fiscalYearId || batch.fiscalPeriodId) ? (
            <p>
              سال مالی:{' '}
              {batch.choices.fiscalYears.find((entry) => entry.id === batch.fiscalYearId)?.label ??
                batch.fiscalYearId ??
                '—'}
              {' — '}دورهٔ مالی:{' '}
              {batch.choices.fiscalPeriods.find((entry) => entry.id === batch.fiscalPeriodId)
                ?.label ??
                batch.fiscalPeriodId ??
                '—'}
            </p>
          ) : null}
          {!batch.actions.prepare && batch.evidence ? (
            <div>
              <strong>شرح تطبیق و مستند بررسی</strong>
              <p style={{ whiteSpace: 'pre-wrap', overflowWrap: 'anywhere' }}>{batch.evidence}</p>
            </div>
          ) : null}
          {batch.actions.prepare ? (
            <>
              <label>
                سال مالی
                <select
                  className="sk-input"
                  value={year}
                  disabled={!editable}
                  onChange={(e) => {
                    setYear(e.target.value);
                    setPeriod('');
                  }}
                >
                  <option value="">انتخاب سال مالی</option>
                  {batch.choices.fiscalYears.map((entry) => (
                    <option key={entry.id} value={entry.id}>
                      {entry.label}
                    </option>
                  ))}
                </select>
              </label>
              <label>
                دورهٔ مالی
                <select
                  className="sk-input"
                  value={period}
                  disabled={!editable || !year}
                  onChange={(e) => setPeriod(e.target.value)}
                >
                  <option value="">انتخاب دورهٔ مالی</option>
                  {batch.choices.fiscalPeriods
                    .filter((entry) => entry.fiscalYearId === year)
                    .map((entry) => (
                      <option key={entry.id} value={entry.id}>
                        {entry.label}
                      </option>
                    ))}
                </select>
              </label>
              <label>
                شرح تطبیق و مستند بررسی
                <textarea
                  className="sk-input"
                  rows={3}
                  maxLength={2000}
                  value={evidence}
                  disabled={!editable}
                  onChange={(e) => setEvidence(e.target.value)}
                />
              </label>
            </>
          ) : null}
        </div>
      </V2Card>
      {batch.items.map((item, index) => {
        const mapping = mappings[item.itemId];
        return (
          <V2Card key={item.itemId}>
            <V2CardHeader
              title={`ردیف ${index + 1} — ${item.kind === 'ar' ? 'دریافتنی' : item.kind === 'ap' ? 'پرداختنی' : 'موجودی'}`}
            />
            <div
              style={{
                padding: 20,
                display: 'flex',
                flexDirection: 'column',
                gap: 12,
              }}
            >
              <p style={{ overflowWrap: 'anywhere' }}>
                مرجع: {item.sourceDocumentReference} — تاریخ افتتاحیه: {item.openingDate}
              </p>
              <p>
                مبلغ ریال: {item.outstandingAmountRial ?? item.totalValueRial ?? '—'}
                {item.kind === 'stock'
                  ? `؛ مقدار دقیق: ${item.quantityNum ?? '—'} / ${item.quantityDen ?? '—'}`
                  : ''}
              </p>
              {!batch.actions.prepare ? (
                <dl>
                  {(['control', 'counterpart'] as const).map((side) => {
                    const accountId = item[`${side}AccountId`];
                    const account = batch.choices.accounts.find((entry) => entry.id === accountId);
                    return (
                      <div key={side}>
                        <dt>{side === 'control' ? 'حساب کنترل' : 'حساب مقابل افتتاحیه'}</dt>
                        <dd>
                          {account ? `${account.code} — ${account.name}` : (accountId ?? '—')}
                        </dd>
                        {(item[`${side}Dimensions`] ?? []).map((entry) => {
                          const dimension = batch.choices.dimensions.find(
                            (d) => d.id === entry.dimensionId,
                          );
                          const value = dimension?.values.find(
                            (v) => v.id === entry.dimensionValueId,
                          );
                          return (
                            <dd key={entry.dimensionId}>
                              {dimension?.label ?? entry.dimensionId}:{' '}
                              {value?.label ?? entry.dimensionValueId}
                            </dd>
                          );
                        })}
                      </div>
                    );
                  })}
                  {item.kind === 'stock' ? (
                    <div>
                      <dt>محل نگهداری</dt>
                      <dd>
                        {batch.choices.warehouseLocations.find(
                          (entry) => entry.id === item.warehouseLocationId,
                        )?.label ??
                          item.warehouseLocationId ??
                          '—'}
                      </dd>
                      {item.lotId ? (
                        <dd>
                          بچ:{' '}
                          {batch.choices.lots.find((entry) => entry.id === item.lotId)?.label ??
                            item.lotId}
                        </dd>
                      ) : null}
                      {item.serialId ? (
                        <dd>
                          سریال:{' '}
                          {batch.choices.serials.find((entry) => entry.id === item.serialId)
                            ?.label ?? item.serialId}
                        </dd>
                      ) : null}
                    </div>
                  ) : null}
                </dl>
              ) : null}
              {batch.actions.prepare ? (
                <>
                  {(['control', 'counterpart'] as const).map((side) => {
                    const accountId = mapping?.[`${side}AccountId`] ?? '';
                    return (
                      <div key={side}>
                        <label>
                          {side === 'control' ? 'حساب کنترل' : 'حساب مقابل افتتاحیه'}
                          <select
                            className="sk-input"
                            value={accountId}
                            disabled={!editable}
                            onChange={(e) =>
                              change(
                                item.itemId,
                                side === 'control'
                                  ? {
                                      controlAccountId: e.target.value,
                                      controlDimensions: {},
                                    }
                                  : {
                                      counterpartAccountId: e.target.value,
                                      counterpartDimensions: {},
                                    },
                              )
                            }
                          >
                            <option value="">انتخاب حساب</option>
                            {batch.choices.accounts.map((account) => (
                              <option key={account.id} value={account.id}>
                                {account.code} — {account.name}
                              </option>
                            ))}
                          </select>
                        </label>
                        {batch.choices.accountDimensionRules
                          .filter((rule) => rule.accountId === accountId)
                          .map((rule) => {
                            const dimension = batch.choices.dimensions.find(
                              (entry) => entry.id === rule.dimensionId,
                            );
                            if (!dimension) return null;
                            return (
                              <label key={rule.dimensionId}>
                                {dimension.label}
                                {rule.required ? ' (الزامی)' : ''}
                                <select
                                  className="sk-input"
                                  disabled={!editable}
                                  value={mapping?.[`${side}Dimensions`]?.[dimension.id] ?? ''}
                                  onChange={(e) =>
                                    change(item.itemId, {
                                      [`${side}Dimensions`]: {
                                        ...mapping?.[`${side}Dimensions`],
                                        [dimension.id]: e.target.value,
                                      },
                                    })
                                  }
                                >
                                  <option value="">انتخاب تفصیلی</option>
                                  {dimension.values.map((value) => (
                                    <option key={value.id} value={value.id}>
                                      {value.label}
                                    </option>
                                  ))}
                                </select>
                              </label>
                            );
                          })}
                      </div>
                    );
                  })}
                  {item.kind === 'stock' ? (
                    <>
                      <label>
                        محل نگهداری
                        <select
                          className="sk-input"
                          disabled={!editable}
                          value={mapping?.warehouseLocationId ?? ''}
                          onChange={(e) =>
                            change(item.itemId, {
                              warehouseLocationId: e.target.value,
                            })
                          }
                        >
                          <option value="">انتخاب محل</option>
                          {batch.choices.warehouseLocations
                            .filter((entry) => entry.warehouseId === item.warehouseId)
                            .map((entry) => (
                              <option key={entry.id} value={entry.id}>
                                {entry.label}
                              </option>
                            ))}
                        </select>
                      </label>
                      {(['lot', 'serial'] as const).map((kind) => {
                        const entries = (
                          kind === 'lot' ? batch.choices.lots : batch.choices.serials
                        ).filter((entry) => entry.productId === item.productId);
                        return entries.length ? (
                          <label key={kind}>
                            {kind === 'lot' ? 'بچ / سری ساخت' : 'سریال'}
                            <select
                              className="sk-input"
                              disabled={!editable}
                              value={mapping?.[`${kind}Id`] ?? ''}
                              onChange={(e) =>
                                change(item.itemId, {
                                  [`${kind}Id`]: e.target.value,
                                })
                              }
                            >
                              <option value="">انتخاب در صورت نیاز کالا</option>
                              {entries.map((entry) => (
                                <option key={entry.id} value={entry.id}>
                                  {entry.label}
                                </option>
                              ))}
                            </select>
                          </label>
                        ) : null;
                      })}
                    </>
                  ) : null}
                </>
              ) : null}
            </div>
          </V2Card>
        );
      })}
      <V2Card>
        <div
          style={{
            padding: 20,
            display: 'flex',
            flexDirection: 'column',
            gap: 12,
          }}
        >
          {batch.actions.post ? (
            <label>
              <input
                type="checkbox"
                checked={confirmed}
                disabled={busy}
                onChange={(e) => setConfirmed(e.target.checked)}
              />{' '}
              اثر قطعی افتتاحیه بر مانده‌های مالی و موجودی را بررسی و تأیید می‌کنم.
            </label>
          ) : null}
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 12 }}>
            {(['prepare', 'submit', 'approve', 'post'] as const)
              .filter((action) => batch.actions[action])
              .map((action) => (
                <V2Button
                  key={action}
                  disabled={
                    busy ||
                    (action === 'post' &&
                      (!confirmed ||
                        (batch.sensitiveAction.requiresMfa && !batch.sensitiveAction.available)))
                  }
                  onClick={() => void act(action)}
                >
                  {busy ? 'در حال انجام…' : actionLabels[action]}
                </V2Button>
              ))}
          </div>
          {!Object.values(batch.actions).some(Boolean) && batch.status !== 'posted' ? (
            <p>
              برای مرحلهٔ بعد، کاربر مجاز مستقل باید این صفحه را باز کند. دسترسی‌ها در سرور بررسی
              می‌شوند.
            </p>
          ) : null}
        </div>
      </V2Card>
    </div>
  );
}
